<template>
  <v-app :key="appStateKey">
    <template>
      <bm-navigation-drawer
        v-if="
          !devicePortalHasContent &&
          isAuthenticatedWithLicense &&
          (!isBillingCancelled || isSupportUser)
        "
        :value.sync="drawer"
        class="no-print"
      />
      <bm-toolbar
        v-if="!devicePortalHasContent && isAuthenticated"
        :only-show-profile-avatar="isBillingCancelled && !isSupportUser"
        class="no-print"
        @toggle-menu="drawer = !drawer"
      />
      <v-main
        :class="{
          // Remove bottom app padding if footer has no content
          'pb-0': !footerHasContent,
        }"
      >
        <portal-target
          name="device-view"
          @change="devicePortalHasContent = $event"
        ></portal-target>
        <router-view
          v-show="!devicePortalHasContent"
          :key="
            $router.currentRoute.meta?.routeKey?.($router.currentRoute) ??
            $router.currentRoute.meta?.key ??
            $router.currentRoute.fullPath
          "
        />
      </v-main>
    </template>

    <bm-footer
      :key="footerRouteKey"
      :height="48"
      :max-height="48"
      @content-updated="footerHasContent = $event"
    />
    <!-- App-wide components. Available for logged in and logged out states -->

    <v-snackbar
      v-show="errorsArray.length > 0"
      :value="errorsArray"
      color="errorMedium"
      :timeout="5000"
    >
      <div v-for="err in errorsArray" :key="err">
        {{ err }}
      </div>
      <v-btn dark text @click="errorsArray = []"> Close</v-btn>
    </v-snackbar>

    <v-snackbar :value="!serviceHealthy" color="errorMedium" :timeout="-1">
      {{ $m.error_connectivity_brewman }}
    </v-snackbar>

    <v-snackbar :value="!connected" color="errorMedium" :timeout="-1">
      {{ $m.error_offline }}
    </v-snackbar>

    <bm-queued-snackbar v-if="snackbarItem" :snack-bar-item="snackbarItem" />

    <bm-error-dialog
      v-if="appStore.state.error"
      :error="appStore.state.error"
      @accept="clearError"
      @cancel="clearError"
    />
    <bm-unauthorised-dialog
      v-if="appStore.state.unauthorisedError"
      :required-permissions="[appStore.state.unauthorisedError]"
      @accept="clearUnauthorisedError"
      @cancel="clearUnauthorisedError"
    />
    <bm-loader />
    <force-logoff-dialog
      v-if="isAuthenticatedNeedingLicense && !isOnBillingPage"
    />

    <bm-welcome-dialog
      v-if="isFirstLogin && showWelcomeDialog"
      @accept="showWelcomeDialog = false"
      @cancel="showWelcomeDialog = false"
    />

    <support-widget v-if="isAuthenticated && user" class="no-print" />

    <modal-dialog-stack />
  </v-app>
</template>

<script setup lang="ts">
import BmNavigationDrawer from "@/components/controls/app/BmNavigationDrawer.vue";
import BmToolbar from "@/components/controls/app/BmToolbar.vue";
import BmLoader from "@/components/dialogs/BmLoader.vue";
import userStore from "@/store/modules/userStore";
import appStore from "@/store/modules/appStore";
import { userSessionService } from "@/app/services/generated/usersessionService";
import { tenantService } from "@/app/services/extensions/tenantServiceExtended";
import BmErrorDialog from "@/components/dialogs/BmErrorDialog.vue";
import BmWelcomeDialog from "@/components/dialogs/BmWelcomeDialog.vue";
import ForceLogoffDialog from "@/components/dialogs/ForceLogoffDialog.vue";
import { BrewManApiError } from "@/app/errors";
import BmFooter from "@/components/controls/app/BmFooter.vue";
import SupportWidget from "@/components/controls/support/SupportWidget.vue";
import { showConfirmationDialog } from "./composables/promptDialogs";
import type { BillingStatus } from "@/app/services/generated/billingplanService";
import { Period } from "@js-joda/core";
import versionManager from "@/store/modules/versionManager";
import router, { pageCounter } from "@/router/router";
import BmQueuedSnackbar from "@/components/controls/snackbar/BmQueuedSnackbar.vue";
import { nextTick, onMounted, watch, computed } from "vue";
import messages, {
  translateWithParams,
  trySetLanguageLocale,
} from "@/plugins/i18n";
import { usePerformanceMeasurements } from "@/app/performanceMeasurements";
import BmUnauthorisedDialog from "@/components/dialogs/BmUnauthorisedDialog.vue";
import { hasFeatureUsingUserStore } from "@/app/featureFlags";
import { EnumFeatureFlag } from "@/app/services/generated/enums/enumFeatureFlag.generated";
import eventShipper from "@/app/eventlogging/eventShipper";
import { makeRef } from "@/components/composables/componentTools";
import { signInWithQuery } from "@/app/ui/navigation/accountManagement";
import { adminRoutes } from "@/app/ui/navigation/administrationMenu";
import { theme } from "@/app/theme";
import ModalDialogStack from "@/components/platform/common/ModalDialogStack.vue";

const errorsArray = makeRef<readonly string[]>([]);
const drawer = makeRef<boolean | undefined>(undefined);
const connected = makeRef(true);
const serviceHealthy = makeRef(true);
const devicePortalHasContent = makeRef(false);
const showWelcomeDialog = makeRef(true);
const footerRouteKey = makeRef(0);
const footerHasContent = makeRef(false);

const snackbarItem = computed(() => appStore.state.snackbarConfigs[0]);

const appStateKey = computed(() => appStore.state.appStateKey);
const userLanguageLocale = computed(() => userStore.state.userLanguageLocale);
const user = computed(() => userStore.state.user);

const isAuthenticated = computed(
  () => userStore.isAuthenticatedWithSelectedTenant.value
);
const billingStatus = computed(
  () => userStore.state.billingStatus as Readonly<BillingStatus> | null
);
const isFirstLogin = computed(() => userStore.state.isFirstLogin);
const isAuthenticatedWithLicense = computed(
  () =>
    userStore.isAuthenticatedWithSelectedTenant.value &&
    userStore.state.hasLicense
);
const isAuthenticatedNeedingLicense = computed(
  () =>
    userStore.isAuthenticatedWithSelectedTenant.value &&
    !userStore.state.hasLicense
);
const isBillingCancelled = computed(
  () => !!billingStatus.value?.planStatus.isCancelled()
);
const isBillingPastDue = computed(() =>
  billingStatus.value?.planStatus.isPastDue()
);
const isSupportUser = computed(
  () => user.value?.readOnly.isSupportUser ?? false
);
const isOnBillingPage = computed(
  () => router.currentRoute.name === adminRoutes.billingDetails.name
);
const documentTitle = computed(() => appStore.state.documentTitle);
const requiresForcedRefresh = computed(
  () => versionManager.requiresForcedRefresh.value
);
const routeKey = computed(() => pageCounter.value);

onMounted(() => {
  setInterval(() => {
    connected.value = navigator.onLine;
  }, 5000);

  // Start a probe that:
  // 1. Checks if the servers are reachable
  // 2. Forwards authentication errors on to global handler to take appropriate action.
  setInterval(async () => {
    if (isAuthenticated.value && isTabVisible()) {
      try {
        await userSessionService.tryGetLicenseForCurrentSession();
        serviceHealthy.value = true;
      } catch (e) {
        // Pass auth exceptions to global handler, so they can be logged
        if (e instanceof BrewManApiError && e.isUnauthenticated) {
          appStore.showError(e);
          return;
        }
        serviceHealthy.value = false;
      }
    }
  }, 10000);

  theme.setModeFromLocalStorage();
});

const devicePortalHasContentWatch = () => {
  // If the user is coming in or out of device view let the nav drawer decide if it needs to show
  drawer.value = undefined;
};

const incrementRouteKey = () => {
  // Defer footer re-render until after the next update cycle to allow child components
  // to re-render as a result of route change (created/mounted/unmounted/destroyed etc).
  // Prevents duplicated portal-targets from being registered after parent component had
  // been destroyed.
  nextTick(() => {
    footerRouteKey.value++;
  });
};

const isAuthenticatedWatcher = async () => {
  if (!isAuthenticated.value && !router.currentRoute.meta?.preSession) {
    // head back to sign in page
    userStore.logSessionState("app watch now not authenticated");
    await eventShipper.flush(2000);

    nextTick(() =>
      router.push(
        signInWithQuery({
          redirect: router.currentRoute.fullPath,
        })
      )
    );
  }
};

const billingStatusWatcher = async () => {
  await checkBillingDialog();
};

const userLanguageLocaleWatcher = async (languageLocale: string) => {
  await trySetLanguageLocale(languageLocale);
};

const documentTitleWatcher = (title: string) => {
  document.title = title;
};

const requiresForcedRefreshWatcher = () => {
  if (requiresForcedRefresh.value) {
    // eslint-disable-next-line no-console
    console.log("Version mismatched for too long. Force reloading...");
    window.location.reload();
  }
};

const isTabVisible = () => document.visibilityState === "visible";

const clearError = () => appStore.showError(null);

const clearUnauthorisedError = () => appStore.clearUnauthorisedError();

const checkBillingDialog = async () => {
  await nextTick();

  if (!billingStatus.value || isOnBillingPage.value) {
    return;
  }

  if (isBillingCancelled.value) {
    await router.push(adminRoutes.billingDetails.location());
    return;
  }

  // How many days from failed payment to allow access
  // The date the access will likely fail the period start is where the initial fail was found
  const expiryDate = tenantService
    .instantToDate(billingStatus.value.currentPeriodStart)
    .plusDays(billingStatus.value.pastDueBillingRetryDays);

  if (
    isAuthenticated.value &&
    isBillingPastDue.value &&
    !hasFeatureUsingUserStore(EnumFeatureFlag.hideBillingPastDueDateWarning) &&
    // Show dialog to show issue with payment (but we will retry for "expiryPeriodDays")
    (await showConfirmationDialog(
      messages.auth_payment_method_problem,
      translateWithParams("auth_payment_method_problem_message", {
        RetryExpiryDate: tenantService.formatDate(expiryDate),
        RetryDays: Period.between(tenantService.today, expiryDate)
          .days()
          .toString(),
      }),
      {
        isBodyHtml: true,
        customConfirmButtonText: messages.auth_payment_method_check,
        width: 600,
      }
    ))
  ) {
    await router.push(adminRoutes.billingDetails.location());
  }
};

watch(devicePortalHasContent, devicePortalHasContentWatch);
watch(routeKey, incrementRouteKey);
watch(isAuthenticated, isAuthenticatedWatcher);
watch(billingStatus, billingStatusWatcher, { immediate: true });
watch(userLanguageLocale, userLanguageLocaleWatcher, { immediate: true });
watch(documentTitle, documentTitleWatcher, { immediate: true });
watch(requiresForcedRefresh, requiresForcedRefreshWatcher);

usePerformanceMeasurements();
</script>
<style lang="sass">
// Hides the scroll bar when not needed
html
  overflow: auto

// @FIXME: Scrolling On Mac Devices issue caused by OSX setting to hide scrollbars when using trackpad
// SEE: https://stackoverflow.com/questions/7855590/preventing-scroll-bars-from-being-hidden-for-macos-trackpad-users-in-webkit-blin
</style>
